import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui';
import { XStack } from '@/components/ui/layout/layout';

import { sentLabel, type PendingEntry } from './connection-actions';
import { RequestLine } from './request-line';
import { handOffFocus } from './request-line-focus';
import { useRunConnectionAction } from './use-run-connection-action';

import type { RequestLines } from './use-request-lines';

/** One request that came in, on one line: who asked, from which address and when, with Accept and Decline. The line
 * closes when the request is declined, and once it has been accepted and the user had a moment to read that. */
export const RequestRow = ({
  request: { person, entry, accountId },
  lines,
}: {
  request: PendingEntry;
  lines: RequestLines;
}) => {
  const { click, decline, isPending } = useRunConnectionAction(
    person,
    'wiki_requests'
  );
  const status = lines.statusOf(accountId);
  // A person who asked from several addresses has a line for each, and the address tells their buttons apart
  const hasSeveralRequests =
    person.littlebirdAccounts.filter(
      ({ connection }) => connection === 'incoming'
    ).length > 1;
  const subject = hasSeveralRequests
    ? `${person.name} (${entry.email})`
    : person.name;
  const sentAt = entry.account?.sentAt;
  const detail = sentAt ? `${entry.email} · ${sentLabel(sentAt)}` : entry.email;

  return (
    <RequestLine
      person={person}
      status={status}
      detail={detail}
      onClosed={() => lines.close(accountId)}
    >
      {status === 'connected' ? (
        <XStack className="items-center gap-1 pr-2 text-xs text-foreground-muted">
          <Icons.check className="size-3.5" />
          Connected
        </XStack>
      ) : (
        <>
          <Button
            size="xs"
            textSize="xs"
            isLoading={isPending}
            aria-label={`Accept ${subject}`}
            onClick={async (event) => {
              const handOff = handOffFocus(event.currentTarget);
              if (
                await lines.answer(accountId, () => click(entry), 'connected')
              ) {
                handOff();
              }
            }}
          >
            Accept
          </Button>
          <Button
            variant="ghost"
            size="xs"
            textSize="xs"
            disabled={isPending}
            aria-label={`Decline ${subject}`}
            onClick={(event) => {
              handOffFocus(event.currentTarget)();
              lines.decline(accountId, (callbacks) =>
                decline(entry, callbacks)
              );
            }}
          >
            Decline
          </Button>
        </>
      )}
    </RequestLine>
  );
};
